import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { OrgTreeSelect } from "@/components/common";
import { useTran } from "@/hooks";
import { useEntryDataStore } from "../../hooks/useDataEntryStore";

/**
 * Drop nodes that have no children in the ORIGINAL tree. A non-leaf whose
 * descendants are all leaves keeps its place (with empty children) so the
 * user can still select it as an aggregation root — the actual rollup
 * happens against the full tree in the store, not this filtered view.
 */
const dropLeaves = (nodes = []) =>
  nodes
    .filter((n) => Array.isArray(n.children) && n.children.length > 0)
    .map((n) => ({ ...n, children: dropLeaves(n.children) }));

/**
 * Organization filter — wraps OrgTreeSelect and pulls the org tree from the
 * shared entryDataStore. `multiSelect` toggles between an `orgIds: string[]`
 * shape (overview) and an `orgId: string` shape (consolidated root).
 *
 * `hideLeaves` (consolidated-only): leaf orgs are hidden from the picker
 * because picking one is meaningless for aggregation — it'd just show that
 * one org's data, which Overview already handles via per-org filtering.
 *
 * Caller is responsible for the right value shape; this component only
 * forwards it through.
 */
const OrgFilter = observer(
  ({
    value,
    onChange,
    multiSelect = false,
    showBranchSelectAll = false,
    branchSelectLevels,
    hideLeaves = false,
    label,
    placeholder,
    helperText,
    showAllOption = true,
    tree: treeOverride,
  }) => {
    const { t } = useTran();
    const { filteredOrgTree } = useEntryDataStore();

    const tree = useMemo(() => {
      const sourceTree = treeOverride || filteredOrgTree;
      return hideLeaves ? dropLeaves(sourceTree) : sourceTree;
    }, [filteredOrgTree, hideLeaves, treeOverride]);

    const resolvedPlaceholder =
      placeholder ||
      (multiSelect ? t("All Organizations") : t("All Organizations"));

    return (
      <div className="w-full sm:w-56 space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          {label || t("Organization")}
        </label>
        <OrgTreeSelect
          tree={tree}
          value={value}
          onChange={onChange}
          placeholder={resolvedPlaceholder}
          multiSelect={multiSelect}
          showBranchSelectAll={showBranchSelectAll}
          branchSelectLevels={branchSelectLevels}
          helperText={helperText}
          showAllOption={showAllOption}
          className="w-full"
        />
      </div>
    );
  },
);

export default OrgFilter;
